import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

/**
 * PORTFOLIO API
 * Todas as chamadas da API relacionadas ao Portfolio Feed
 */

// ============================================
// CREATE & UPDATE
// ============================================

/**
 * Criar novo post de portfólio
 */
export const createPortfolioPost = async (data, token) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/portfolio`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Atualizar post de portfólio
 */
export const updatePortfolioPost = async (postId, data, token) => {
  try {
    const response = await axios.put(
      `${API_URL}/api/portfolio/${postId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Deletar post de portfólio
 */
export const deletePortfolioPost = async (postId, token) => {
  try {
    const response = await axios.delete(
      `${API_URL}/api/portfolio/${postId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ============================================
// READ
// ============================================

/**
 * Buscar feed de portfólio
 */
export const getPortfolioFeed = async (params = {}) => {
  try {
    const response = await axios.get(`${API_URL}/api/portfolio/feed`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Buscar post específico de portfólio
 */
export const getPortfolioPost = async (postId) => {
  try {
    const response = await axios.get(`${API_URL}/api/portfolio/${postId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Buscar portfólio de um usuário
 */
export const getUserPortfolio = async (username, params = {}) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/portfolio/user/${username}`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Buscar categorias de portfólio
 */
export const getPortfolioCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/portfolio/categories`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Buscar portfólios
 */
export const searchPortfolio = async (params = {}) => {
  try {
    const response = await axios.get(`${API_URL}/api/portfolio/search`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Buscar estatísticas do portfólio do usuário
 */
export const getMyPortfolioStats = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/api/portfolio/my/stats`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ============================================
// ACTIONS
// ============================================

/**
 * Alternar destaque do projeto
 */
export const toggleFeatured = async (postId, token) => {
  try {
    const response = await axios.patch(
      `${API_URL}/api/portfolio/${postId}/featured`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ============================================
// INTERACTIONS
// ============================================

/**
 * Curtir um projeto
 */
export const likePortfolioPost = async (postId, token) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/portfolio/${postId}/like`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Descurtir um projeto
 */
export const unlikePortfolioPost = async (postId, token) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/portfolio/${postId}/unlike`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Compartilhar um projeto
 */
export const sharePortfolioPost = async (postId, platform = 'direct', token) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/portfolio/${postId}/share`,
      { platform },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Adicionar comentário
 */
export const addCommentToPortfolio = async (postId, text, token) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/portfolio/${postId}/comment`,
      { text },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Buscar comentários de um projeto
 */
export const getPortfolioComments = async (postId, params = {}) => {
  try {
    const response = await axios.get(`${API_URL}/api/portfolio/${postId}/comments`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Deletar comentário
 */
export const deletePortfolioComment = async (postId, commentId, token) => {
  try {
    const response = await axios.delete(
      `${API_URL}/api/portfolio/${postId}/comment/${commentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Responder a um comentário
 */
export const replyToPortfolioComment = async (postId, commentId, text, token) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/portfolio/${postId}/comment/${commentId}/reply`,
      { text },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
